import { useState, useEffect, type FormEvent } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { knoxApi, KnoxApiError } from '@/lib/knox-api';
import { AuthPageShell } from '@/pages/login/AuthPageShell';
import { loadRecaptchaScript, executeRecaptcha } from '@/lib/recaptcha';
import RecaptchaCheckbox from '@/components/RecaptchaCheckbox';

const RECAPTCHA_SITE_KEY = (window as any).__KNOX_CONFIG__?.RECAPTCHA_SCORE_SITE_KEY || undefined;
const RECAPTCHA_CHECKBOX_SITE_KEY = (window as any).__KNOX_CONFIG__?.RECAPTCHA_CHECKBOX_SITE_KEY || undefined;

const inputClass =
  'w-full rounded-full border border-black/[0.16] bg-surface-container-lowest py-4 px-6 text-foreground placeholder:text-muted-foreground/40 focus:border-primary/30 focus:outline-none focus:ring-2 focus:ring-primary/10 transition-all duration-300';

const inputReadonlyClass =
  'w-full rounded-full border border-black/[0.08] bg-surface-container py-4 px-6 text-foreground/60 cursor-default select-none';

const btnPrimaryClass =
  'w-full rounded-full bg-gradient-to-br from-[#0070F3] to-[#005ac6] py-4 font-bold text-white shadow-lg transition-all duration-300 hover:shadow-[0_0_20px_rgba(135,173,255,0.3)] active:scale-[0.98] disabled:opacity-50 disabled:cursor-not-allowed';

export function ForgotPasswordPage() {
  const { t } = useTranslation('knox-aiops');
  const location = useLocation();
  const prefillEmail = (location.state as { email?: string } | null)?.email ?? '';

  const [email, setEmail] = useState(prefillEmail);
  const [submitted, setSubmitted] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [needChallenge, setNeedChallenge] = useState(false);
  const [challengeToken, setChallengeToken] = useState('');
  const [resendCountdown, setResendCountdown] = useState(0);

  const isEmailPrefilled = Boolean(prefillEmail);

  // Load reCAPTCHA score-based script on mount
  useEffect(() => {
    if (RECAPTCHA_SITE_KEY) {
      loadRecaptchaScript(RECAPTCHA_SITE_KEY).catch(() => {});
    }
  }, []);

  // Countdown timer for resend button
  useEffect(() => {
    if (resendCountdown <= 0) return;
    const t = setTimeout(() => setResendCountdown(c => c - 1), 1000);
    return () => clearTimeout(t);
  }, [resendCountdown]);

  // Auto-retry when checkbox token is obtained
  useEffect(() => {
    if (!challengeToken) return;
    const retry = async () => {
      setSubmitting(true);
      try {
        await knoxApi.forgotPassword(email, { recaptchaToken: challengeToken, recaptchaType: 'checkbox' });
        setSubmitted(true);
        setResendCountdown(60);
      } catch {
        setSubmitted(true); // anti-enumeration: always show success
        setResendCountdown(60);
      } finally {
        setSubmitting(false);
        setNeedChallenge(false);
        setChallengeToken('');
      }
    };
    retry();
  }, [challengeToken, email]);

  async function handleSubmit(e: FormEvent) {
    e.preventDefault();
    if (!email) return;
    setNeedChallenge(false);
    setSubmitting(true);
    let challenged = false;
    try {
      let recaptchaToken: string | undefined;
      if (RECAPTCHA_SITE_KEY) {
        try { recaptchaToken = await executeRecaptcha('forgot_password'); } catch { /* not ready */ }
      }
      await knoxApi.forgotPassword(email, { recaptchaToken });
    } catch (err) {
      if (err instanceof KnoxApiError && err.code === 'NEED_CHALLENGE') {
        setNeedChallenge(true);
        challenged = true;
      }
      // Other errors: still show success (anti-enumeration)
    } finally {
      setSubmitting(false);
      if (!challenged) {
        setSubmitted(true);
        setResendCountdown(60);
      }
    }
  }

  if (submitted) {
    return (
      <AuthPageShell>
        <div className="flex flex-col items-center gap-6 text-center">
          <img src="/knox-logo.png" alt="Knox" className="h-10 sm:h-12 w-auto" />
          <div>
            <h2 className="font-heading text-2xl sm:text-3xl font-extrabold tracking-tight text-foreground mb-2">
              {t('forgotPassword.checkEmail')}
            </h2>
            <p className="text-sm text-muted-foreground">
              {t('forgotPassword.checkEmailDescription')}
            </p>
          </div>
          <button
            type="button"
            disabled={resendCountdown > 0}
            onClick={async () => {
              setResendCountdown(60);
              try {
                let recaptchaToken: string | undefined;
                if (RECAPTCHA_SITE_KEY) {
                  try { recaptchaToken = await executeRecaptcha('forgot_password'); } catch { /* not ready */ }
                }
                await knoxApi.forgotPassword(email, { recaptchaToken });
              } catch { /* silent — anti-enumeration */ }
            }}
            className={btnPrimaryClass}
          >
            {resendCountdown > 0
              ? t('forgotPassword.resendIn', { seconds: resendCountdown })
              : t('forgotPassword.resend')}
          </button>
          <Link to="/login" className="text-sm text-primary hover:underline underline-offset-4">
            {t('forgotPassword.backToLogin')}
          </Link>
        </div>
      </AuthPageShell>
    );
  }

  return (
    <AuthPageShell>
      <div className="mb-6 sm:mb-8 text-center">
        <div className="mb-3 flex justify-center">
          <img src="/knox-logo.png" alt="" className="h-10 sm:h-12 w-auto" />
        </div>
        <h1 className="font-heading text-2xl sm:text-3xl font-extrabold tracking-tight text-foreground mb-1">
          {t('forgotPassword.title')}
        </h1>
        <p className="text-sm text-muted-foreground">
          {t('forgotPassword.subtitle')}
        </p>
      </div>

      <form onSubmit={handleSubmit} className="flex flex-col gap-6">
        <div>
          <label
            htmlFor="forgot-email"
            className="mb-3 block px-1 text-[0.6875rem] font-bold uppercase tracking-widest text-muted-foreground"
          >
            {t('loginForm.email')}
          </label>
          {isEmailPrefilled ? (
            <div className={inputReadonlyClass}>{email}</div>
          ) : (
            <input
              id="forgot-email"
              type="email"
              autoComplete="email"
              required
              autoFocus
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              placeholder={t('loginForm.emailPlaceholder')}
              className={inputClass}
            />
          )}
        </div>

        {needChallenge && RECAPTCHA_CHECKBOX_SITE_KEY && (
          <RecaptchaCheckbox
            siteKey={RECAPTCHA_CHECKBOX_SITE_KEY}
            action="forgot_password"
            onVerified={(token) => {
              setChallengeToken(token);
              setNeedChallenge(false);
            }}
          />
        )}

        <button
          type="submit"
          disabled={!email || submitting || needChallenge}
          className={btnPrimaryClass}
        >
          {submitting ? t('forgotPassword.sending') : t('forgotPassword.send')}
        </button>
      </form>

      <div className="text-center mt-4">
        <Link to="/login" className="text-xs text-primary hover:underline underline-offset-4">
          {t('forgotPassword.backToLogin')}
        </Link>
      </div>
    </AuthPageShell>
  );
}
